import * as React from 'react';
import type {CodeIslandSize, CodeIslandState} from '../CodeIslandSharedUtils';
import {
  getLastKnownSizeStorageKey,
  parseStoredSize,
} from '../CodeIslandSharedUtils';
import CodeIslandCustomElementStore from './CodeIslandCustomElementStore';
import {
  clearLocalDevOverrides,
  setLocalDevOverride,
} from './localDevOverrideRegistry';
import type {ComponentStateScenario} from '@packages/systems/code-libraries/component-states';

// Identity cache — useSyncExternalStore compares snapshots with Object.is,
// so parsing a fresh object on every render would re-render endlessly
const persistedSizeCache = new Map<
  string,
  {raw: string; size: CodeIslandSize | null}
>();

function readLastKnownSize(
  id: string | null | undefined
): CodeIslandSize | null {
  if (!id) return null;
  const store = CodeIslandCustomElementStore.getInstance();
  const storedSize =
    store && typeof store.getLastKnownSize === 'function'
      ? store.getLastKnownSize(id)
      : null;
  return storedSize || readPersistedSize(id) || null;
}

function readPersistedSize(id: string): CodeIslandSize | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage?.getItem(getLastKnownSizeStorageKey(id)) ?? null;
  } catch {
    return null;
  }
  if (raw === null) return null;

  const cached = persistedSizeCache.get(id);
  if (cached && cached.raw === raw) return cached.size;

  const size = parseStoredSize(raw);
  persistedSizeCache.set(id, {raw, size});
  return size;
}

/**
 * Hook to get the state of a code island custom element.
 *
 * @param {string | null} id - The ID of the code island element. This may be null because we rely on the data-w-id attribute,
 * which is only available in the designer. In published sites, this won't be available, but that's okay
 * because we handle code island render states server-side for SSR.
 *
 * @returns {CodeIslandState} The current state of the code island element
 */
export function useCodeIslandCustomElementState(
  id: string | null | undefined
): CodeIslandState | null {
  return React.useSyncExternalStore(
    (onStoreChange) => {
      if (id) {
        const store = CodeIslandCustomElementStore.getInstance();
        if (store) {
          return store.subscribe(onStoreChange);
        }
      }
      return () => {};
    },
    () => {
      let result: CodeIslandState | undefined;
      if (id) {
        const store = CodeIslandCustomElementStore.getInstance();
        if (store) {
          result = store.get(id);
        }
      }
      return result || null;
    },
    () => null
  );
}

/**
 * Last known rendered size of a code island, for sizing the loading
 * placeholder. Falls back to the persisted size, then to the component
 * definition's size (recorded by any instance).
 */
export function useCodeIslandLastKnownSize(
  id: string | null | undefined,
  definitionId?: string | null
): CodeIslandSize | null {
  return React.useSyncExternalStore(
    (onStoreChange) => {
      if (id) {
        const store = CodeIslandCustomElementStore.getInstance();
        if (store) {
          return store.subscribe(onStoreChange);
        }
      }
      return () => {};
    },
    () => {
      if (!id) return null;
      // Instance-level sizes always win over definition-level ones
      return readLastKnownSize(id) || readLastKnownSize(definitionId);
    },
    () => null
  );
}

export function useUpdateCodeIslandCustomElementState(
  id: string | null | undefined
): (newState: CodeIslandState) => void {
  return React.useCallback(
    (newState: CodeIslandState) => {
      if (id) {
        const store = CodeIslandCustomElementStore.getInstance();
        if (store) {
          store.update(id, newState);
        }
      }
    },
    [id]
  );
}

export function useReloadCodeIslandCustomElement(
  id: string | null | undefined,
  onReload?: () => void
) {
  return React.useCallback(() => {
    if (id) {
      const store = CodeIslandCustomElementStore.getInstance();
      if (store) {
        store.update(id, {status: 'loading'});
        store.incrementRefreshCounter(id);
        // Call callback to trigger redraws if provided (e.g., dispatch Flux action)
        // Only call if store is available to ensure we actually updated the state
        if (onReload) {
          onReload();
        }
      }
    }
  }, [id, onReload]);
}

/**
 * Hook to get the refresh counter for a code island element.
 * The refresh counter is used to force React to remount the component by changing its key.
 *
 * @param {string | null} id - The ID of the code island element
 *
 * @returns {number} The current refresh counter
 */
export function useCodeIslandRefreshCounter(
  id: string | null | undefined
): number {
  return React.useSyncExternalStore(
    (onStoreChange) => {
      if (id) {
        const store = CodeIslandCustomElementStore.getInstance();
        if (store) {
          return store.subscribe(onStoreChange);
        }
      }
      return () => {};
    },
    () => {
      if (id) {
        const store = CodeIslandCustomElementStore.getInstance();
        if (
          store &&
          store.getRefreshCounter &&
          typeof store.getRefreshCounter === 'function'
        ) {
          return store.getRefreshCounter(id);
        }
      }
      return 0;
    },
    () => 0
  );
}

/**
 * Hook to increment the refresh counter for a code island element, forcing a remount.
 *
 * @param {string | null} id - The ID of the code island element
 *
 * @returns {Function} A function to increment the refresh counter
 */
export function useIncrementCodeIslandRefreshCounter(
  id: string | null | undefined
): () => void {
  return React.useCallback(() => {
    if (id) {
      const store = CodeIslandCustomElementStore.getInstance();
      if (
        store &&
        store.incrementRefreshCounter &&
        typeof store.incrementRefreshCounter === 'function'
      ) {
        store.incrementRefreshCounter(id);
      }
    }
  }, [id]);
}

/**
 * Hook for the Designer to register and clear local dev overrides, which
 * redirect a real component's bundle to a local dev server's. Registering
 * creates the store when no island has yet, as `MultiStatePreviewBridgeEngine`
 * does for scenario bindings. Neither call remounts islands; use `remountAll`.
 *
 * @returns {Object} Stable functions to set one override and to clear them all
 */
export function useLocalDevOverrideActions() {
  return React.useMemo(
    () => ({setLocalDevOverride, clearLocalDevOverrides}),
    []
  );
}

/**
 * Hook to read the multi-state preview scenario binding for a single
 * code-island instance. Returns `null` when the instance is not part of
 * a multi-state preview frame (the default variant-frame case).
 *
 */
export function useCodeIslandScenarioBinding(
  designerInstanceId: string | null | undefined
): ComponentStateScenario | null {
  return React.useSyncExternalStore(
    (onStoreChange) => {
      if (!designerInstanceId) return () => {};
      const store = CodeIslandCustomElementStore.getInstance();
      if (store) {
        return store.subscribe(onStoreChange);
      }
      return () => {};
    },
    () => {
      if (!designerInstanceId) return null;
      const store = CodeIslandCustomElementStore.getInstance();
      if (!store || typeof store.getScenarioBinding !== 'function') {
        return null;
      }
      return store.getScenarioBinding(designerInstanceId);
    },
    () => null
  );
}
